import React from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Typography,
} from '@mui/material';
import { useAuth } from '../App';
import { apiRequest } from '../api';

interface Product {
  id: number;
  type: string;
  txt_type: string;
  periodic: string;
  price: number;
  start: string;
  ende: string;
  next: string;
}

type SortField = 'product' | 'periodic' | 'price' | 'start' | 'next' | 'status';
type SortDirection = 'asc' | 'desc';

const formatDate = (value: string) => {
  if (!value || value.startsWith('0001-') || value.startsWith('0000-')) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getFullYear()}`;
};

const isActive = (product: Product) => {
  if (!product.ende || product.ende.startsWith('0001-') || product.ende.startsWith('0000-')) return true;
  return new Date(product.ende).getTime() > new Date().setHours(0, 0, 0, 0);
};

const periodLabels: Record<string, string> = { '-': 'Einmalig', d: 'Täglich', m: 'Monatlich', y: 'Jährlich' };

const ProduktePage: React.FC = () => {
  const { token } = useAuth();
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<Product | null>(null);
  const [cancelling, setCancelling] = React.useState(false);
  const [sortField, setSortField] = React.useState<SortField>('product');
  const [sortDirection, setSortDirection] = React.useState<SortDirection>('asc');

  const loadProducts = React.useCallback(async () => {
    if (!token) return;
    try {
      setProducts(await apiRequest<Product[]>('/account/products', token));
      setError(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Produkte konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => { void loadProducts(); }, [loadProducts]);

  const visibleProducts = React.useMemo(() => products
    .filter((product) => Number(product.price) >= 0)
    .slice()
    .sort((first, second) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      let comparison = 0;
      switch (sortField) {
        case 'product':
          comparison = (first.txt_type || first.type).localeCompare(second.txt_type || second.type, 'de');
          break;
        case 'periodic':
          comparison = (periodLabels[first.periodic] ?? first.periodic).localeCompare(periodLabels[second.periodic] ?? second.periodic, 'de');
          break;
        case 'price':
          comparison = Number(first.price) - Number(second.price);
          break;
        case 'start':
        case 'next':
          comparison = new Date(first[sortField]).getTime() - new Date(second[sortField]).getTime();
          break;
        case 'status':
          comparison = Number(isActive(first)) - Number(isActive(second));
          break;
        default:
          break;
      }
      return comparison * direction;
    }), [products, sortDirection, sortField]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((direction) => direction === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortableHeader = (field: SortField, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={sortField === field ? sortDirection : false}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const cancelProduct = async () => {
    if (!token || !selected) return;
    setCancelling(true);
    try {
      await apiRequest<{ message: string }>(`/account/products/${selected.id}/cancel`, token, { method: 'POST' });
      setSelected(null);
      await loadProducts();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Kündigung fehlgeschlagen.');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>Produkte</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Ihre gebuchten Produkte und laufenden Leistungen.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              {sortableHeader('product', 'Produkt')}
              {sortableHeader('periodic', 'Abrechnung')}
              {sortableHeader('price', 'Preis', 'right')}
              {sortableHeader('start', 'Beginn')}
              {sortableHeader('next', 'Nächste Abrechnung')}
              {sortableHeader('status', 'Status')}
              <TableCell align="right">Aktion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={28} sx={{ my: 2 }} /></TableCell></TableRow>
            ) : visibleProducts.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">Es sind keine Produkte hinterlegt.</TableCell></TableRow>
            ) : visibleProducts.map((product) => {
              const active = isActive(product);
              return (
                <TableRow key={product.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{product.txt_type || product.type}</Typography>
                    <Typography variant="caption" color="text.secondary">{product.type}</Typography>
                  </TableCell>
                  <TableCell>{periodLabels[product.periodic] ?? product.periodic}</TableCell>
                  <TableCell align="right">{Number(product.price).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</TableCell>
                  <TableCell>{formatDate(product.start)}</TableCell>
                  <TableCell>{formatDate(product.next)}</TableCell>
                  <TableCell>{active ? 'Aktiv' : `Beendet am ${formatDate(product.ende)}`}</TableCell>
                  <TableCell align="right">
                    <Button color="error" variant="outlined" size="small" disabled={!active} onClick={() => setSelected(product)}>
                      Kündigen
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={Boolean(selected)} onClose={() => !cancelling && setSelected(null)}>
        <DialogTitle>Produkt kündigen?</DialogTitle>
        <DialogContent>
          <Typography>Die Kündigung für „{selected?.txt_type}“ wird zum ende der Vertagslaufzeit wirksam. Das Produkt bleibt aus Nachweisgründen in Ihrem Verlauf erhalten.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)} disabled={cancelling}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={cancelProduct} disabled={cancelling}>
            {cancelling ? 'Wird gekündigt …' : 'Jetzt kündigen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProduktePage;
